/**
 * ECharts Option Builder — Scatter chart.
 *
 * Handles both numeric X (true scatter plot) and categorical X
 * (string dimensions like country, product — renders as jittered dots
 * on a category axis).
 */
import type { EChartsOption } from 'echarts';
import { type ChartTheme, buildBaseOption, tooltipConfig, legendConfig, xAxisConfig, yAxisConfig } from '../theme';
import type { ChartData } from '../../../../api/types';
import { colIdx, colType, uniqueValues, axisLabelFormatter, estimateYAxisNameGap } from './utils';

// ── Helpers ────────────────────────────────────────────────────────────────

/** True when the column holds numeric data that can be plotted on a value axis. */
function isNumericType(type: string): boolean {
  return ['number', 'integer', 'float', 'double', 'decimal', 'bigint', 'numeric', 'int4', 'int8', 'float4', 'float8'].includes(type.toLowerCase());
}

// ── Builder ────────────────────────────────────────────────────────────────

export function buildScatterChart(
  data: ChartData,
  theme: ChartTheme,
  animated: boolean,
): EChartsOption {
  const meta = data.meta;
  const xCol = meta?.x ?? data.columns[0].name;
  const yCol = meta?.y?.[0] ?? data.columns[1]?.name ?? data.columns[0].name;
  const seriesCol = meta?.series ?? undefined;
  const sizeCol = meta?.size ?? undefined;

  const xIdx = colIdx(data, xCol);
  const yIdx = colIdx(data, yCol);
  const xType = colType(data, xCol);
  const yType = colType(data, yCol);
  const sizeIdx = sizeCol ? colIdx(data, sizeCol) : -1;

  // Detect whether X axis is numeric or categorical
  const xIsNumeric = isNumericType(xType);

  // For categorical X, build a sorted category list
  // Normalize null/undefined the same way as xValue() below
  const categories = !xIsNumeric ? uniqueValues(data, xCol).map((v) => String(v ?? '')) : [];

  // When a size column is present, compute min/max for scaling symbol size
  let sizeMin = Infinity;
  let sizeMax = -Infinity;
  if (sizeIdx >= 0) {
    for (const row of data.rows) {
      const v = Number(row[sizeIdx] ?? 0);
      if (v < sizeMin) sizeMin = v;
      if (v > sizeMax) sizeMax = v;
    }
    if (!isFinite(sizeMin)) sizeMin = 0;
    if (!isFinite(sizeMax)) sizeMax = 1;
  }
  const sizeRange = sizeMax - sizeMin || 1;
  const minSymbol = 4;
  const maxSymbol = 30;

  const symbolSize = sizeIdx >= 0
    ? (val: number[]) => {
        const raw = val[2] ?? 0;
        return minSymbol + ((raw - sizeMin) / sizeRange) * (maxSymbol - minSymbol);
      }
    : 8;

  const scatterBase = {
    symbolSize,
    itemStyle: { opacity: 0.65 },
    emphasis: { focus: 'none' as const, scale: false },
  };

  /** Convert a raw X value to a plottable coordinate.
   *  Numeric → number; categorical → category index. */
  const xValue = (raw: unknown): number | string => {
    if (xIsNumeric) return Number(raw ?? 0);
    return String(raw ?? '');
  };

  let series: any[];

  if (seriesCol) {
    const sIdx = colIdx(data, seriesCol);
    const groups = new Map<string, (number | string)[][]>();
    for (const row of data.rows) {
      const sName = String(row[sIdx] ?? 'Data');
      if (!groups.has(sName)) groups.set(sName, []);
      const point: (number | string)[] = [xValue(row[xIdx]), Number(row[yIdx] ?? 0)];
      if (sizeIdx >= 0) point.push(Number(row[sizeIdx] ?? 0));
      groups.get(sName)!.push(point);
    }

    series = Array.from(groups.entries()).map(([name, points]) => ({
      type: 'scatter' as const,
      name,
      data: points,
      ...scatterBase,
    }));
  } else {
    const points = data.rows.map((row) => {
      const point: (number | string)[] = [xValue(row[xIdx]), Number(row[yIdx] ?? 0)];
      if (sizeIdx >= 0) point.push(Number(row[sizeIdx] ?? 0));
      return point;
    });
    series = [
      {
        type: 'scatter' as const,
        name: yCol,
        data: points,
        large: true,
        largeThreshold: 2000,
        ...scatterBase,
      },
    ];
  }

  // Build X axis config — value axis for numeric, category axis for strings
  const xAxisOpt = xIsNumeric
    ? {
        ...xAxisConfig(theme),
        type: 'value' as const,
        name: xCol,
        axisLabel: { ...xAxisConfig(theme).axisLabel, formatter: axisLabelFormatter(xType) },
        splitLine: { lineStyle: { color: theme.gridLineColor, width: 1 } },
      }
    : {
        ...xAxisConfig(theme),
        type: 'category' as const,
        name: xCol,
        data: categories,
        axisLabel: { ...xAxisConfig(theme).axisLabel },
      };

  return {
    ...buildBaseOption(theme, animated),
    tooltip: tooltipConfig(theme, 'item'),
    legend: { ...legendConfig(theme), show: !!seriesCol },
    xAxis: xAxisOpt,
    yAxis: {
      ...yAxisConfig(theme),
      name: yCol,
      nameGap: estimateYAxisNameGap(
        new Map([['y', data.rows.map((r) => Number(r[yIdx] ?? 0))]]),
      ),
      axisLabel: { ...yAxisConfig(theme).axisLabel, formatter: axisLabelFormatter(yType) },
    },
    series,
  };
}